import React, {useEffect, useRef, useState} from "react";
import {
    Button,
    Grid,
    Link,
    Paper, TableCell, TableRow,
} from "@material-ui/core"
import {useRefreshableLocation} from "../../../../../hooks";
import useFetchableData from "../../../../../hooks/useFetchableData";
import RectangularSkeleton from "../../../shared/Skeleton/RectangularSkeleton";
import SortableTable from "../../../shared/Tables/SortableTable";
import FormDialog from "../../../shared/Dialogs/FormDialog";
import LinkedinSyncConfirmDialog from "../../../shared/Dialogs/LinkedinSyncConfirmDialog";
import LinkedinUnsyncWarningDialog from "../../../shared/Dialogs/LinkedinUnsyncWarningDialog";
import MatchReviewModal from "../../../shared/MatchReviewModal";
import useSnackbar from "../../../../../hooks/useSnackbar";
import {useLinkedinCrossDatasetConfirm} from "../../../../../hooks/useLinkedinCrossDatasetConfirm";
import useInvestorUpdater from "../../hooks/useInvestorUpdater";
import useInvestorDeleter from "../../hooks/useInvestorDeleter";
import CustomSnackbar from "../../../../user/shared/CustomSnackbar";
import Contact from "./Contact";
import {affinitySections, blankSelectionReason} from "../../../../../helpers/affinityRowValidation";
import contactReviewCard from "./contactReviewCard";


const ContactsSection = (
    {
        isEditing, handleOnChangeInField, investorId,
        handleOnError, session, genders, contactResponsibilities,
        fetchContacts, setUserMadeChanges, userMadeChanges, drafts=false, investorName, ...props
    }
) => {
    const [contactsErrors, setContactsErrors] = useState([]);
    const [selectedContact, setSelectedContact] = useState(null);
    // Per contact id, how many times its row was reverted - see revertContact.
    const [revertedContacts, setRevertedContacts] = useState({});
    const [contacts, setContacts] = useState([]);
    const {data: contactsData, initialFetch, fetchData} = useFetchableData(fetchContacts, drafts ? "contact_drafts" : "contacts");
    const snackbar = useSnackbar();
    const updater = useInvestorUpdater(session, snackbar, drafts);
    const deleter = useInvestorDeleter(session, snackbar, drafts);
    const originalContactsRef = useRef({});
    const {confirmLinkedinChange, linkedinConfirmDialogProps, linkedinUnsyncWarningDialogProps, linkedinReviewModalProps} = useLinkedinCrossDatasetConfirm('Contact', session);
    const [dataType, setDataType] = useState('fintrx_data');
    const handleQueryParams = (queryParams) => {
        const {
            data_type, contact_id
        } = queryParams;
        if(data_type) {
            setDataType(data_type);
        }
        if(contact_id) {
            setSelectedContact(parseInt(contact_id));
        } else {
            setSelectedContact(null);
        }
    }
    const {refresh: refreshQueryParams} = useRefreshableLocation(handleQueryParams);

    useEffect(() => {
        const hasNewContacts = !!contacts.find(c => c.id === undefined)
        handleOnChangeInField("contacts_attributes", [...contacts], initialFetch, !hasNewContacts)
    }, [contacts])

    useEffect(() => {
        setContacts(contactsData)
        setContactsErrors(Array(contactsData.length).fill(false))
        originalContactsRef.current = contactsData.reduce((byId, contact) => ({...byId, [contact.id]: contact}), {});
    }, [contactsData])

    const blankAffinityReason = (contact) => blankSelectionReason(affinitySections(contact.company, contact.university));

    const setContactError = (index, error) => {
        let newErrors = [...contactsErrors];
        newErrors[index] = error;
        setContactsErrors(newErrors);
    }

    useEffect(() => {
        handleOnError(contactsErrors.some((error) => error))
    }, [contactsErrors]);

    const handleAddContact = () => {
        let newContacts = [...contacts];

        newContacts.push({
            contact_notes: '',
            gender: '',
            first_name: '',
            last_name: '',
            name_suffix: '',
            position_id: undefined,
            linkedin_url: '',
            achievements: [],
            achievement_ids: [],
            email: '',
            phone: '',
            investment_committee: false,
            biography: '',
            additional_notes: '',
            private_bio: '',
            contact_responsibilities_ids: [],
            university_ids: [],
            company_ids: [],
            university: [],
            company: [],
            key: Date.now(),
            id: undefined,
        })

        setContacts(newContacts);
        setContactsErrors(prevState => ([...prevState, true]));
    }

    const handleRemoveContact = async (event, contact) => {
        event.stopPropagation();
        if (window.confirm(`Are you sure you want to delete ${contact.first_name} ${contact.last_name}?`)) {
            await deleter.contacts(contact);
            const newContacts = contacts.filter((c) => c.id !== contact.id);
            const deletedIndex = contacts.findIndex((c) => c.id === contact.id);
            const newContactErrors = contactsErrors.filter((error, index) => index !== deletedIndex);
            setContacts(newContacts);
            setContactsErrors(newContactErrors);
            handleOnError(false);
            setSelectedContact(null);
        }
    }

    // The modal writes straight into `contacts`, which the table renders from, so closing has to
    // put the row back. Otherwise a declined edit - import match and all - is saved by the next
    // Save Changes. The row's Contact keeps its own copy in local state and writes it back on the
    // next edit, so bumping its key remounts it on the restored row instead.
    const revertContact = (index, contactId) => {
        const original = originalContactsRef.current[contactId];
        if (!original) {
            return;
        }

        setContacts((prevContacts) => prevContacts.map(
            (contact, i) => (i === index ? {...original} : contact)
        ));
        setRevertedContacts((prevState) => ({
            ...prevState, [contactId]: (prevState[contactId] || 0) + 1,
        }));
    }

    const handleCloseContact = (index, contactId) => {
        setUserMadeChanges(false);
        setSelectedContact(null);
        revertContact(index, contactId);
    }

    const handleSaveContact = async (contact) => {
        const original = originalContactsRef.current[contact.id];
        const linkedinAction = await confirmLinkedinChange(
            contact.datasets, original?.linkedin_url, contact.linkedin_url,
            contact.dataset_contact_names,
            {
                importingMatch: !!contact.matched_people_id,
                importedValue: contact.matched_linkedin_url,
                review: {
                    peopleId: contact.matched_people_id || contact.people_id,
                    recordId: contact.id,
                    formCard: contactReviewCard(contact, investorName),
                },
            }
        );
        if (linkedinAction === 'abort') {
            return;
        }
        setUserMadeChanges(false);
        const payload = linkedinAction
            ? {...contact, unsync_person: linkedinAction === 'unsync'}
            : contact;
        await updater.contacts(payload);
        setSelectedContact(null);
        fetchData();
    }

    const tableContacts = contacts.filter((contact) => !contact._destroy && !contact.key);
    const removeError = (index) => setContactsErrors(contactsErrors.filter((error, i) => index !== i));

    return (
        <Grid container item xs={12}>
            <RectangularSkeleton loading={!initialFetch} width={1000} height={300}>
                <Paper className="form-container" elevation={3}>
                    <Grid item xs={12}>
                        <h3>Contacts</h3>
                    </Grid>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            {
                                tableContacts.length > 0 && <SortableTable
                                    headCells={[
                                        { id: 'first_name', numeric: false, disablePadding: false, label: 'First Name', sortable: true },
                                        { id: 'last_name', numeric: false, disablePadding: false, label: 'Last Name', sortable: true },
                                        { id: 'email', numeric: false, disablePadding: false, label: 'Email', sortable: true },
                                        { id: 'position_name', numeric: false, disablePadding: false, label: 'Title', sortable: true },
                                        { id: 'options', numeric: false, disablePadding: false, label: null },
                                    ]}
                                    rows={tableContacts}
                                    RowRenderer={
                                        (row, _isSelected, index, _handleCheck) => (<TableRow
                                            hover
                                            tabIndex={-1}
                                            key={row.id}
                                            style={{cursor: "pointer"}}
                                            onClick={(event) => setSelectedContact(row.id)}
                                        >
                                            <TableCell>
                                                {row.first_name}
                                            </TableCell>
                                            <TableCell>
                                                {row.last_name}
                                            </TableCell>
                                            <TableCell>
                                                {row.email}
                                            </TableCell>
                                            <TableCell>
                                                {row.position_name}
                                            </TableCell>
                                            <TableCell>
                                                <Link onClick={(event) => handleRemoveContact(event, row)}>
                                                    Delete
                                                </Link>
                                            </TableCell>
                                        </TableRow>)
                                    }
                                />
                            }
                        </Grid>
                        <Grid item xs={12}>
                            <Button color="primary" variant="contained" onClick={handleAddContact} style={{marginBottom: '20px'}}>Add New Contact {drafts ? "Draft" : ""}</Button>
                        </Grid>
                        <Grid item xs={12}>
                            {
                                contacts.map((contact, index) => (<React.Fragment key={`${contact.id || contact.key}-${revertedContacts[contact.id] || 0}`}>
                                        { !!contact.key ? <Contact
                                                session={session}
                                                contactData={contact}
                                                investorName={investorName}
                                                index={index}
                                                contacts={contacts}
                                                setContacts={setContacts}
                                                removeContactError={(i) => removeError(i)}
                                                genders={genders}
                                                responsibilities={contactResponsibilities}
                                                isEditing={isEditing}
                                                handleOnError={(error) => setContactError(index, error)}
                                                error={contactsErrors[index]}
                                                drafts={drafts}
                                                snackbar={snackbar}
                                                dataType={dataType}
                                                deleter={deleter}
                                                investorId={investorId}
                                                isNew
                                                setUserMadeChanges={setUserMadeChanges}
                                            /> : <FormDialog
                                                open={selectedContact === contact.id}
                                                label={"Contact Form"}
                                                handleSave={() => handleSaveContact(contact)}
                                                handleClose={() => handleCloseContact(index, contact.id)}
                                                disabled={!userMadeChanges || !!blankAffinityReason(contact)}
                                                disabledReason={blankAffinityReason(contact)}
                                            >
                                                <Contact
                                                    session={session}
                                                    contactData={contact}
                                                    investorName={investorName}
                                                    index={index}
                                                    contacts={contacts}
                                                    setContacts={setContacts}
                                                    removeContactError={(i) => removeError(i)}
                                                    genders={genders}
                                                    responsibilities={contactResponsibilities}
                                                    isEditing={isEditing}
                                                    handleOnError={(error) => setContactError(index, error)}
                                                    error={contactsErrors[index]}
                                                    drafts={drafts}
                                                    setSelectedContact={setSelectedContact}
                                                    setUserMadeChanges={setUserMadeChanges}
                                                    snackbar={snackbar}
                                                    dataType={dataType}
                                                    deleter={deleter}
                                                    investorId={investorId}
                                                    originalLinkedinUrl={originalContactsRef.current[contact.id]?.linkedin_url}
                                                />
                                            </FormDialog>
                                        }

                                    </React.Fragment>)
                                )
                            }
                        </Grid>

                        {/* One snackbar for the whole section: inside the map it rendered once per
                            contact, stacking identical toasts, and the hardcoded severity showed
                            every warning in the success colour. */}
                        <CustomSnackbar
                            open={snackbar.open}
                            closeHandler={snackbar.close}
                            message={snackbar.message}
                            stayOpen={snackbar.stayOpen}
                            options={{severity: snackbar.severity}}
                        />
                    </Grid>
                </Paper>
            </RectangularSkeleton>

            <LinkedinSyncConfirmDialog {...linkedinConfirmDialogProps} />
            <MatchReviewModal {...linkedinReviewModalProps} />
            <LinkedinUnsyncWarningDialog {...linkedinUnsyncWarningDialogProps} />
        </Grid>
    )
}

export default ContactsSection;